/**
 * Chair generator — parametric seating compound generator.
 *
 * Emits a group whose children are independently selectable, material-able
 * parts: seat, backrest, legs (or sled runners or pedestal), and optional
 * armrests.
 *
 * Coordinate convention (Z-up, matching the rest of PoseRef):
 *   X = width (left–right), Y = depth (front–back), Z = height (up)
 *
 * Local anchor: the parent's origin sits at the geometric centre of the
 * chair's footprint at Z = 0 (floor level). All children are offset from
 * there, so a chair placed at the 3D cursor lands cleanly on the ground.
 *
 * Back angle: the backrest pivots about its bottom edge, which sits at the
 * junction of the seat top and the rear of the seat. Positive angles tilt
 * the top of the back away from the sitter — the standard relaxed-chair
 * posture. Implemented as `localTransform.rotation[0]` (X-axis Euler) on
 * the back child, so the full angle range is live-editable without
 * rebuilding any other part.
 *
 * Leg styles:
 *   four_leg  — four vertical box legs at the seat corners.
 *   sled      — two side runners (ground rail + front/rear uprights) that
 *               give the chair a rocker/sled base silhouette.
 *   pedestal  — single central column rising from a flat square base,
 *               like a restaurant or café chair.
 *
 * Armrests (when enabled): a vertical post rising from the seat surface
 * to the pad height, capped with a horizontal pad that spans the armrest
 * depth. The posts sit just outside the seat width so they don't overlap
 * the seat geometry.
 *
 * Slat back: when `backStyle = "slats"`, the solid back panel is replaced
 * by N evenly-spaced horizontal slats. The slats are distributed edge-to-
 * edge across `backHeight`; when the total slat thickness exceeds the back
 * height the gap clamps to zero (slats overlap rather than going negative).
 */

import type { PrimitiveGeometry } from "../primitives.ts";
import type { BoxSpec } from "./generatorGeom.ts";
import { buildBox, concatBoxes } from "./generatorGeom.ts";
import type {
  Generator,
  GeneratorBuildContext,
  GeneratorChild,
  GeneratorLocalTransform,
  GeneratorOutput,
  GeneratorParamSpec,
  GeneratorParams,
} from "./types.ts";
import {
  boolParam,
  defineGenerator,
  enumParam,
  intParam,
  numParam,
} from "./types.ts";

// ---------------------------------------------------------------------------
// Params interface
// ---------------------------------------------------------------------------

interface ChairParams extends GeneratorParams {
  seatWidth: number;
  seatDepth: number;
  seatHeight: number;
  seatThickness: number;
  backHeight: number;
  backAngle: number;
  backStyle: string;
  backThickness: number;
  slatCount: number;
  slatThickness: number;
  legStyle: string;
  legThickness: number;
  hasArmrests: boolean;
  armrestHeight: number;
  armrestDepth: number;
  armrestThickness: number;
}

// ---------------------------------------------------------------------------
// Parameter schema
// ---------------------------------------------------------------------------

const CHAIR_SPECS: readonly GeneratorParamSpec[] = [
  // Standard dining chair seat: ~46 × 44 cm at 46 cm height (EN 1335 / ANSI BIFMA).
  {
    type: "section",
    id: "chair-seat",
    label: "Seat",
    params: [
      {
        id: "seatWidth",
        label: "Width",
        type: "number",
        unit: "meters",
        default: 0.46,
        min: 0.3,
        max: 1.2,
        step: 0.01,
      },
      {
        id: "seatDepth",
        label: "Depth",
        type: "number",
        unit: "meters",
        default: 0.44,
        min: 0.3,
        max: 1.0,
        step: 0.01,
      },
      {
        id: "seatHeight",
        label: "Height",
        type: "number",
        unit: "meters",
        default: 0.46,
        min: 0.3,
        max: 0.9,
        step: 0.01,
      },
      {
        id: "seatThickness",
        label: "Thickness",
        type: "number",
        unit: "meters",
        default: 0.04,
        min: 0.01,
        max: 0.15,
        step: 0.005,
      },
    ],
  },
  // 38 cm back height → top rail at ~84 cm (standard dining chair).
  // 5° back angle gives a comfortable resting posture at default params.
  {
    type: "section",
    id: "chair-back",
    label: "Back",
    params: [
      {
        id: "backHeight",
        label: "Height",
        type: "number",
        unit: "meters",
        default: 0.38,
        min: 0.1,
        max: 1.0,
        step: 0.01,
      },
      {
        id: "backAngle",
        label: "Angle",
        type: "number",
        unit: "degrees",
        default: 5,
        min: 0,
        max: 30,
        step: 1,
      },
      {
        id: "backStyle",
        label: "Style",
        type: "enum",
        default: "solid",
        options: [
          { value: "solid", label: "Solid Panel" },
          { value: "slats", label: "Horizontal Slats" },
        ],
      },
      {
        id: "backThickness",
        label: "Thickness",
        type: "number",
        unit: "meters",
        default: 0.025,
        min: 0.01,
        max: 0.1,
        step: 0.005,
      },
      {
        id: "slatCount",
        label: "Slat Count",
        type: "number",
        unit: "count",
        default: 4,
        min: 2,
        max: 12,
        step: 1,
        showIf: { id: "backStyle", value: "slats" },
      },
      {
        id: "slatThickness",
        label: "Slat Thickness",
        type: "number",
        unit: "meters",
        default: 0.015,
        min: 0.005,
        max: 0.06,
        step: 0.005,
        showIf: { id: "backStyle", value: "slats" },
      },
    ],
  },
  {
    type: "section",
    id: "chair-legs",
    label: "Legs",
    params: [
      {
        id: "legStyle",
        label: "Style",
        type: "enum",
        default: "four_leg",
        options: [
          { value: "four_leg", label: "Four Legs" },
          { value: "sled", label: "Sled Base" },
          { value: "pedestal", label: "Pedestal" },
        ],
      },
      {
        id: "legThickness",
        label: "Thickness",
        type: "number",
        unit: "meters",
        default: 0.04,
        min: 0.01,
        max: 0.12,
        step: 0.005,
      },
    ],
  },
  // 22 cm above seat matches ANSI/BIFMA ergonomic armrest height guidance.
  {
    type: "section",
    id: "chair-armrests",
    label: "Armrests",
    params: [
      { id: "hasArmrests", label: "Armrests", type: "boolean", default: false },
      {
        id: "armrestHeight",
        label: "Height",
        type: "number",
        unit: "meters",
        default: 0.22,
        min: 0.1,
        max: 0.5,
        step: 0.01,
        showIf: { id: "hasArmrests", value: true },
      },
      {
        id: "armrestDepth",
        label: "Depth",
        type: "number",
        unit: "meters",
        default: 0.22,
        min: 0.1,
        max: 0.5,
        step: 0.01,
        showIf: { id: "hasArmrests", value: true },
      },
      {
        id: "armrestThickness",
        label: "Thickness",
        type: "number",
        unit: "meters",
        default: 0.04,
        min: 0.01,
        max: 0.1,
        step: 0.005,
        showIf: { id: "hasArmrests", value: true },
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

/** Identity-rotation local transform from a position. */
function lt(pos: [number, number, number]): GeneratorLocalTransform {
  return { position: pos, rotation: [0, 0, 0], scale: [1, 1, 1] };
}

/**
 * Back geometry in child-local space.
 *
 * Child origin = hinge point at the bottom of the backrest (sits at the
 * top-rear of the seat in world space). Geometry extends from Z = 0 up to
 * Z = backHeight so rotation around the child origin naturally pivots the
 * back about the seat-top junction.
 *
 * Solid panel — one box spanning the full back height.
 * Slat style  — N thin horizontal boxes distributed edge-to-edge within
 *               backHeight, with equal gaps between them. When total slat
 *               thickness exceeds backHeight, the gap clamps to zero.
 */
function buildBackGeometry(
  seatWidth: number,
  backHeight: number,
  backThickness: number,
  backStyle: string,
  slatCount: number,
  slatThickness: number,
): PrimitiveGeometry {
  if (backStyle === "slats") {
    const n = Math.max(1, slatCount);
    const gap =
      n > 1 ? Math.max(0, (backHeight - n * slatThickness) / (n - 1)) : 0;
    const supportW = Math.max(backThickness, Math.min(seatWidth * 0.08, 0.04));
    const supportX = seatWidth * 0.5 - supportW * 0.5;
    const slatSpan = Math.max(0.01, seatWidth - supportW * 2);
    const boxes: BoxSpec[] = [];
    boxes.push({
      cx: -supportX,
      cy: 0,
      cz: backHeight * 0.5,
      sx: supportW,
      sy: backThickness,
      sz: backHeight,
    });
    boxes.push({
      cx: supportX,
      cy: 0,
      cz: backHeight * 0.5,
      sx: supportW,
      sy: backThickness,
      sz: backHeight,
    });
    for (let i = 0; i < n; i++) {
      const cz = slatThickness * 0.5 + i * (slatThickness + gap);
      boxes.push({
        cx: 0,
        cy: 0,
        cz,
        sx: slatSpan,
        sy: backThickness,
        sz: slatThickness,
      });
    }
    return concatBoxes(boxes);
  }
  // Solid — centred at (0, 0, backHeight/2) so the bottom face is at Z = 0.
  return concatBoxes([
    {
      cx: 0,
      cy: 0,
      cz: backHeight * 0.5,
      sx: seatWidth,
      sy: backThickness,
      sz: backHeight,
    },
  ]);
}

/**
 * Sled runner geometry in child-local space.
 *
 * Each runner (one per side) is three boxes:
 *   • a flat ground rail running the full depth plus a small overreach,
 *   • a front upright connecting the rail to the seat front corner,
 *   • a rear upright connecting the rail to the seat rear corner.
 *
 * The child is positioned at the runner's centre X (±runnerX), Y = 0, Z = 0.
 * All geometry lives at cx = 0 in child space (along the X axis the runner
 * is just legThickness thick, positioned by the child's world X).
 */
function buildSledGeometry(
  seatDepth: number,
  seatHeight: number,
  seatThickness: number,
  legThickness: number,
): PrimitiveGeometry {
  const legH = Math.max(0.001, seatHeight - seatThickness);
  const railH = Math.max(0.01, legThickness * 0.5);
  const uprH = Math.max(0.001, legH - railH);
  // Rail extends legThickness beyond each upright fore and aft, giving the
  // characteristic sled overhang at the front and back.
  const overreach = legThickness;
  const railLen = seatDepth + 2 * overreach;
  const uprY = railLen * 0.5 - legThickness * 0.5;

  return concatBoxes([
    // Ground rail
    { cx: 0, cy: 0, cz: railH * 0.5, sx: legThickness, sy: railLen, sz: railH },
    // Front upright
    {
      cx: 0,
      cy: uprY,
      cz: railH + uprH * 0.5,
      sx: legThickness,
      sy: legThickness,
      sz: uprH,
    },
    // Rear upright
    {
      cx: 0,
      cy: -uprY,
      cz: railH + uprH * 0.5,
      sx: legThickness,
      sy: legThickness,
      sz: uprH,
    },
  ]);
}

/**
 * Pedestal geometry at the group origin.
 *
 * A square column rising from a flat square base. The base is 0.05 m tall
 * and `baseR` wide; the column diameter equals `legThickness`. Proportions
 * are sized so the pedestal looks stable relative to the default seat size.
 */
function buildPedestalGeometry(
  seatHeight: number,
  seatThickness: number,
  legThickness: number,
): PrimitiveGeometry {
  const baseH = 0.05;
  const baseR = Math.max(legThickness + 0.01, 0.25); // at least 25 cm radius
  const colH = Math.max(0.001, seatHeight - seatThickness - baseH);

  return concatBoxes([
    // Central column
    {
      cx: 0,
      cy: 0,
      cz: baseH + colH * 0.5,
      sx: legThickness * 2,
      sy: legThickness * 2,
      sz: colH,
    },
    // Base
    { cx: 0, cy: 0, cz: baseH * 0.5, sx: baseR * 2, sy: baseR * 2, sz: baseH },
  ]);
}

// ---------------------------------------------------------------------------
// Chair generator
// ---------------------------------------------------------------------------

export const CHAIR_GENERATOR: Generator = defineGenerator<ChairParams>({
  id: "chair",
  label: "Chair",
  category: "prop",

  paramSpecs: CHAIR_SPECS,
  defaultParams: {
    seatWidth: 0.46,
    seatDepth: 0.44,
    seatHeight: 0.46,
    seatThickness: 0.04,
    backHeight: 0.38,
    backAngle: 5,
    backStyle: "solid",
    backThickness: 0.025,
    slatCount: 4,
    slatThickness: 0.015,
    legStyle: "four_leg",
    legThickness: 0.04,
    hasArmrests: false,
    armrestHeight: 0.22,
    armrestDepth: 0.22,
    armrestThickness: 0.04,
  },

  build(p: ChairParams, _ctx: GeneratorBuildContext): GeneratorOutput {
    const seatW = numParam(p, "seatWidth", 0.46);
    const seatD = numParam(p, "seatDepth", 0.44);
    const seatH = numParam(p, "seatHeight", 0.46);
    const seatT = numParam(p, "seatThickness", 0.04);
    const backH = numParam(p, "backHeight", 0.38);
    const backAng = numParam(p, "backAngle", 5) * (Math.PI / 180);
    const backSty = enumParam(p, "backStyle", "solid");
    const backT = numParam(p, "backThickness", 0.025);
    const slatCt = intParam(p, "slatCount", 4);
    const slatT = numParam(p, "slatThickness", 0.015);
    const legSty = enumParam(p, "legStyle", "four_leg");
    const legT = numParam(p, "legThickness", 0.04);
    const hasArms = boolParam(p, "hasArmrests", false);
    const armH = numParam(p, "armrestHeight", 0.22);
    const armD = numParam(p, "armrestDepth", 0.22);
    const armT = numParam(p, "armrestThickness", 0.04);

    const children: GeneratorChild[] = [];

    // ── Seat ──────────────────────────────────────────────────────────────
    // Box centred at X = 0, Y = 0, Z = seatH − seatT/2.
    // Top face aligns exactly with Z = seatH.
    children.push({
      roleId: "seat",
      name: "Seat",
      objectKind: "cube",
      geometry: buildBox(seatW, seatD, seatT),
      localTransform: lt([0, 0, seatH - seatT * 0.5]),
    });

    // ── Backrest ──────────────────────────────────────────────────────────
    // Child origin = hinge at the rear of the seat top.
    //
    // Y placement: we want the back's +Y face (facing the sitter) flush with
    // the seat's rear edge at Y = −seatD/2. The geometry is centred at Y = 0
    // in child space, so the +Y face sits at +backT/2 in child space.
    //   child_Y + backT/2 = −seatD/2  →  child_Y = −(seatD/2 + backT/2)
    //
    // Rotation[0] = backAng: positive X-axis Euler in Z-up tilts the +Z
    // direction toward −Y (away from sitter) — standard reclining geometry.
    children.push({
      roleId: "back",
      name: "Back",
      objectKind: "cube",
      geometry: buildBackGeometry(seatW, backH, backT, backSty, slatCt, slatT),
      localTransform: {
        position: [0, -(seatD * 0.5 + backT * 0.5), seatH],
        rotation: [backAng, 0, 0],
        scale: [1, 1, 1],
      },
    });

    // ── Legs ──────────────────────────────────────────────────────────────
    const legH = Math.max(0.001, seatH - seatT); // floor → seat bottom

    if (legSty === "four_leg") {
      // Legs inset legT/2 from each seat edge so their outer faces are flush
      // with the seat perimeter. This creates a clean sight line where the
      // seat overhangs the legs by exactly 0 — intentional for a block chair.
      const lx = seatW * 0.5 - legT * 0.5;
      const ly = seatD * 0.5 - legT * 0.5;
      const lz = legH * 0.5;
      const geo = buildBox(legT, legT, legH);
      for (const [rid, name, sx, sy] of [
        ["leg_fl", "Leg FL", -lx, ly],
        ["leg_fr", "Leg FR", lx, ly],
        ["leg_bl", "Leg BL", -lx, -ly],
        ["leg_br", "Leg BR", lx, -ly],
      ] as [string, string, number, number][]) {
        children.push({
          roleId: rid,
          name,
          objectKind: "cube",
          geometry: geo,
          localTransform: lt([sx, sy, lz]),
        });
      }
    } else if (legSty === "sled") {
      // Runners positioned so their outer X face aligns with the seat edge.
      const geo = buildSledGeometry(seatD, seatH, seatT, legT);
      const rx = seatW * 0.5 - legT * 0.5;
      children.push({
        roleId: "sled_l",
        name: "Sled L",
        objectKind: "cube",
        geometry: geo,
        localTransform: lt([-rx, 0, 0]),
      });
      children.push({
        roleId: "sled_r",
        name: "Sled R",
        objectKind: "cube",
        geometry: geo,
        localTransform: lt([rx, 0, 0]),
      });
    } else {
      // pedestal
      children.push({
        roleId: "pedestal",
        name: "Pedestal",
        objectKind: "cube",
        geometry: buildPedestalGeometry(seatH, seatT, legT),
        localTransform: lt([0, 0, 0]),
      });
    }

    // ── Armrests ──────────────────────────────────────────────────────────
    // Each armrest is two boxes in child-local space (child origin at the
    // outer seat edge at seat-top height):
    //   • a vertical support post from Z = 0 up to Z = armH,
    //   • a horizontal pad from Z = armH to Z = armH + armT.
    //
    // The post sits just outside the seat (armT/2 from the seat edge) so
    // there is no geometry overlap with the seat or legs.
    if (hasArms) {
      const armGeo = concatBoxes([
        // Support post
        { cx: 0, cy: 0, cz: armH * 0.5, sx: armT, sy: armT, sz: armH },
        // Horizontal pad
        { cx: 0, cy: 0, cz: armH + armT * 0.5, sx: armT, sy: armD, sz: armT },
      ]);
      const ax = seatW * 0.5 + armT * 0.5;
      children.push({
        roleId: "armrest_l",
        name: "Armrest L",
        objectKind: "cube",
        geometry: armGeo,
        localTransform: lt([-ax, 0, seatH]),
      });
      children.push({
        roleId: "armrest_r",
        name: "Armrest R",
        objectKind: "cube",
        geometry: armGeo,
        localTransform: lt([ax, 0, seatH]),
      });
    }

    return {
      kind: "group",
      parent: { name: "Chair" },
      children,
    };
  },
});
